import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $watchlist, RegimePills } from '../../entities/market';
import {
  formatMoney,
  formatPercent,
  formatRatio,
  placesOf,
  toNumber,
} from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { PlValue } from '../../shared/ui';

/** One tile per watchlist instrument: price, day change, drawdown and regime (CLAUDE.md §4). */
export function MarketStrip() {
  const { t } = useTranslation();
  const locale = useLocale();
  const watchlist = useUnit($watchlist);
  if (!watchlist?.length) return null;
  return (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
      role="group"
      aria-label={t('marketStrip.label')}
    >
      {watchlist.map(({ instrument, quote, regime }) => {
        const change = quote?.day_change_pct ?? null;
        return (
          <div
            key={instrument.id}
            className="min-w-0 rounded-lg border border-border-subtle bg-bg-1 px-4 py-3"
          >
            <p className="flex items-baseline gap-2 truncate text-xs text-text-3">
              <span className="font-medium text-text-2">{instrument.display_symbol}</span>
              <span className="truncate">{instrument.name}</span>
            </p>
            <p className="tabular mt-1 truncate text-lg font-semibold">
              {quote
                ? formatMoney(quote.price, locale, { places: placesOf(instrument.tick_size) })
                : '—'}
            </p>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-text-3">
              {change !== null ? (
                <PlValue value={toNumber(change)}>
                  {formatPercent(change, locale, { signed: true })}
                </PlValue>
              ) : (
                <span>—</span>
              )}
              {regime && (
                <span className="tabular">
                  {t('marketStrip.fromHigh', { drawdown: formatRatio(regime.drawdown, locale) })}
                </span>
              )}
            </div>
            <div className="mt-2">
              <RegimePills regime={regime} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
